/**
 * Hostname-aware route guard (Substep 2e).
 *
 * Returns `'customer'` when the SPA is served at a consumer hostname
 * (`my.onestamp.id`), `'staff'` otherwise. Drives App.js route
 * partitioning + the 401 redirect target.
 *
 * Mechanism: a single, top-of-module read of `window.location.hostname`.
 * Hostname does not change intra-session, so we resolve once at import
 * time and export the constant + a function for places that may import
 * before `window` is available (SSR-safe).
 *
 * QA escape hatch:
 *   `?host=customer|staff` query param is honoured ONLY when the
 *   current hostname is NOT in the production allowlist. Production
 *   hosts ignore the override — so a leaked QA URL can never weaken
 *   production behaviour.
 *
 * No localStorage toggle (Mandate — Decision §e). The query param
 * deliberately decays on internal <Link> navigation (Decision §l):
 * react-router does not preserve query strings across links, and we
 * don't paper over that.
 */

const CUSTOMER_HOSTS = new Set(['my.onestamp.id']);
const STAFF_HOSTS = new Set(['staff.onestamp.id', 'onestamp.id']);

function isProductionHost(hostname) {
  return CUSTOMER_HOSTS.has(hostname) || STAFF_HOSTS.has(hostname);
}

function readOverride() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const v = qs.get('host');
    if (v === 'customer' || v === 'staff') return v;
  } catch {
    /* ignore — malformed URL or missing global */
  }
  return null;
}

/**
 * Resolve the host mode for the current window. Safe to call from
 * non-browser contexts (returns `'staff'` — the maximally permissive
 * mode that preserves today's behaviour).
 */
export function detectHostMode() {
  if (typeof window === 'undefined') return 'staff';
  const hostname = (window.location.hostname || '').toLowerCase();

  // QA override — only on non-production hosts.
  if (!isProductionHost(hostname)) {
    const override = readOverride();
    if (override) return override;
  }

  if (CUSTOMER_HOSTS.has(hostname)) return 'customer';
  if (STAFF_HOSTS.has(hostname)) return 'staff';

  // Unknown host → staff (preview, localhost, anything else).
  return 'staff';
}

// Module-level cache so callers can read the value without invoking a
// function. Both forms are exported so tests can re-import if they
// stub the URL.
export const HOST_MODE = detectHostMode();
export const IS_CUSTOMER_HOST = HOST_MODE === 'customer';
export const IS_STAFF_HOST = HOST_MODE === 'staff';

// Exposed for inspection in dev / tests; do not write to these in app code.
export const __ALLOWLISTS = {
  customer: [...CUSTOMER_HOSTS],
  staff: [...STAFF_HOSTS],
};
